import cx from 'classnames'
import dynamic from 'next/dynamic'
import NextImage from 'next/image'
import { CSSProperties, useCallback, useContext, useState } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import {
  SanityBorderRadius,
  SanityImageFragment,
} from '@data/sanity/queries/types/image'
import {
  SanityMuxVideo,
  SanityVideo,
  SanityVideoAspectRatio,
  SanityVideoAspectRatioValue,
  SanityVideoType,
} from '@data/sanity/queries/types/video'
import {
  getImageDimensions,
  getSanityImageLoader,
  getSanityImageUrl,
} from '@lib/image'
import { SiteContext } from '@lib/site-context'
import {
  aspectRatioPaddingClasses,
  getMuxVideoAspectRatio,
  MuxPlayerProps,
} from '@lib/video'
import { borderRadiusClassMap } from '@lib/border'

const MuxPlayer = dynamic(() => import('./mux-player'))

type MuxVideoProps = Omit<MuxPlayerProps, 'assetDocument'> & {
  video: SanityMuxVideo
  aspectRatio?: SanityVideoAspectRatio
  thumbnailImage?: SanityImageFragment
  borderRadius?: SanityBorderRadius
}

const getVideoAspectRatioClass = (
  aspectRatio?: SanityVideoAspectRatioValue,
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl',
) => {
  if (!aspectRatio) {
    return ''
  }

  return `${breakpoint ? `${breakpoint}:` : ''}${
    aspectRatioPaddingClasses[aspectRatio]
  }`
}

const MuxVideo = ({
  video,
  showControls,
  showCustomControls,
  autoplay,
  autoplayOnHover,
  openModalOnClick,
  muted,
  loop,
  aspectRatio,
  borderRadius,
  thumbnailImage,
  className,
}: MuxVideoProps) => {
  const { toggleVideoModal } = useContext(SiteContext)

  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.1,
  })

  const [isThumbnailVisible, setIsThumbnailVisible] = useState(true)

  const videoAspectRatio = getMuxVideoAspectRatio(video)

  const handleClick = () => {
    if (openModalOnClick) {
      const sanityVideo: SanityVideo = {
        type: SanityVideoType.MUX,
        muxVideo: video,
        settings: {
          autoplay,
          autoplayOnHover,
          controls: showControls,
          loop,
          muted,
          openModalOnClick,
        },
      }
      toggleVideoModal(true, sanityVideo)
    }
  }

  const hasCustomAspectRatio = Boolean(aspectRatio?.custom)
  const aspectRatioClasses = hasCustomAspectRatio
    ? [
        getVideoAspectRatioClass(
          aspectRatio?.base ?? SanityVideoAspectRatioValue.ASPECT_9_16,
        ),
        getVideoAspectRatioClass(
          aspectRatio?.sm ?? SanityVideoAspectRatioValue.ASPECT_16_9,
          'sm',
        ),
        getVideoAspectRatioClass(aspectRatio?.md, 'md'),
        getVideoAspectRatioClass(aspectRatio?.lg, 'lg'),
        getVideoAspectRatioClass(aspectRatio?.xl, 'xl'),
      ]
    : []
  const aspectRatioStyle: CSSProperties = !hasCustomAspectRatio
    ? {
        paddingTop: `${100 / videoAspectRatio}%`,
      }
    : {}

  const thumbnailImageDimensions = thumbnailImage
    ? getImageDimensions(thumbnailImage)
    : null
  const thumbnailImageUrl = getSanityImageUrl(thumbnailImage)

  const handleShowThumbnail = useCallback(() => {
    setIsThumbnailVisible(true)
  }, [])

  const handleHideThumbnail = useCallback(() => {
    setIsThumbnailVisible(false)
  }, [])

  return (
    <div
      ref={ref}
      className={cx(
        'relative w-full overflow-hidden z-0',
        borderRadius ? borderRadiusClassMap[borderRadius] : '',
        ...aspectRatioClasses,
        className,
      )}
      style={{
        ...aspectRatioStyle,
      }}
    >
      {!!thumbnailImage && !!autoplayOnHover && (
        <NextImage
          src={JSON.stringify(thumbnailImage)}
          loader={getSanityImageLoader}
          placeholder={
            thumbnailImage.lqip as `data:image/${string}` | undefined
          }
          width={thumbnailImageDimensions?.width}
          height={thumbnailImageDimensions?.height}
          quality={75}
          alt=""
          className={cx(
            'absolute inset-0 z-10 w-full h-full object-cover pointer-events-none',
            {
              'opacity-0': !isThumbnailVisible,
            },
          )}
        />
      )}

      {isIntersecting && (
        <MuxPlayer
          assetDocument={video.asset}
          showControls={showControls}
          showCustomControls={showCustomControls}
          autoplay={autoplay}
          autoplayOnHover={autoplayOnHover}
          openModalOnClick={openModalOnClick}
          muted={muted}
          loop={loop}
          playsInline={autoplay && !showControls}
          customPosterUrl={thumbnailImageUrl}
          className={cx({
            'cursor-pointer': !!openModalOnClick,
          })}
          videoStyle={{
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100%',
            width: '100%',
          }}
          onClick={handleClick}
          onShowThumbnail={handleShowThumbnail}
          onHideThumbnail={handleHideThumbnail}
        />
      )}
    </div>
  )
}

export default MuxVideo
